import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { ArrowRight, ChevronLeft, ChevronRight, Search, User } from 'lucide-react'
import {
  Company,
  PersonSummary,
  PersonSearchResult,
  SearchResult,
  searchCompanies,
  searchPersons,
} from '../services/apiClient'
import { Container, CountryFlagIcon, Switch, Tabs } from '../components/ui'
import type { TabDef } from '../components/ui'
import WatchlistIndicator from '../components/watchlist/WatchlistIndicator'
import {
  companyCountryFlagCode,
  detectCountry,
  formatAddressCountryLabel,
  formatCompanyAddress,
  formatDate,
  formatStatusLabel,
  isDissolvedStatus,
  isDistressedStatus,
  stripTrailingCountryFromAddress,
} from '../utils/format'
import { usePageTitle } from '../hooks/usePageTitle'

const CVR_NUMBER_PATTERN = /^\d{8}$/
const PAGE_SIZE = 20
type TabId = 'companies' | 'persons'

function parsePageParam(raw: string | null): number {
  const n = parseInt(raw || '1', 10)
  if (!Number.isFinite(n) || n < 1) return 1
  return Math.floor(n)
}

/**
 * Mixed search results — companies and persons shown in a tabbed view.
 * Both searches run in parallel on every query change; switching tabs is
 * instant and costs no extra network request.
 *
 * URL params:
 *   ?q=      — the search query (shared)
 *   ?type=   — active tab: "companies" | "persons" (omitted when "companies")
 *   ?page=   — company page (omitted when 1)
 *   ?ppage=  — person page  (omitted when 1)
 *   ?includeInactive=1 — include dissolved companies (companies tab only)
 *
 * Typing 8 digits still jumps straight to the company profile.
 */
const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const query       = searchParams.get('q') || ''
  const activeTab   = (searchParams.get('type') as TabId | null) ?? 'companies'
  const companyPage = useMemo(() => parsePageParam(searchParams.get('page')),  [searchParams])
  const personPage  = useMemo(() => parsePageParam(searchParams.get('ppage')), [searchParams])
  const includeInactive = searchParams.get('includeInactive') === '1'

  const [draft, setDraft] = useState(query)
  const [companyResult, setCompanyResult] = useState<SearchResult>({
    companies: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE,
  })
  const [personResult, setPersonResult] = useState<PersonSearchResult>({
    persons: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE,
  })
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [loadingPersons,   setLoadingPersons]   = useState(false)
  const [errorCompanies,   setErrorCompanies]   = useState<string | null>(null)
  const [errorPersons,     setErrorPersons]     = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const pageTitle = useMemo(
    () => (query.trim() ? `Søgning: ${query.trim()}` : 'Søgning'),
    [query]
  )
  usePageTitle(pageTitle)

  // Company search — re-runs when query or page changes
  useEffect(() => {
    setDraft(query)
    if (!query) {
      setCompanyResult({ companies: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE })
      setErrorCompanies(null)
      return
    }
    let cancelled = false
    setLoadingCompanies(true)
    setErrorCompanies(null)
    searchCompanies(query, companyPage, PAGE_SIZE, includeInactive)
      .then((data) => { if (!cancelled) setCompanyResult(data) })
      .catch((err) => {
        if (!cancelled) {
          setErrorCompanies(err?.message || 'Søgning fejlede.')
          setCompanyResult({ companies: [], totalCount: 0, page: companyPage, pageSize: PAGE_SIZE })
        }
      })
      .finally(() => { if (!cancelled) setLoadingCompanies(false) })
    return () => { cancelled = true }
  }, [query, companyPage, includeInactive])

  // Person search — also runs in the background so the count is ready when you switch tab
  useEffect(() => {
    if (!query) {
      setPersonResult({ persons: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE })
      setErrorPersons(null)
      return
    }
    let cancelled = false
    setLoadingPersons(true)
    setErrorPersons(null)
    searchPersons(query, personPage, PAGE_SIZE)
      .then((data) => { if (!cancelled) setPersonResult(data) })
      .catch((err) => {
        if (!cancelled) {
          setErrorPersons(err?.message || 'Søgning fejlede.')
          setPersonResult({ persons: [], totalCount: 0, page: personPage, pageSize: PAGE_SIZE })
        }
      })
      .finally(() => { if (!cancelled) setLoadingPersons(false) })
    return () => { cancelled = true }
  }, [query, personPage])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const q = draft.trim()
    if (!q) return
    if (CVR_NUMBER_PATTERN.test(q)) { navigate(`/company/${q}`); return }
    // New query: reset to tab "companies", page 1
    setSearchParams((prev) => {
      const p = new URLSearchParams()
      p.set('q', q)
      if (prev.get('includeInactive') === '1') p.set('includeInactive', '1')
      return p
    })
  }

  function setIncludeInactive(next: boolean) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      if (next) p.set('includeInactive', '1')
      else p.delete('includeInactive')
      p.delete('page')
      return p
    })
  }

  function setTab(id: TabId) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      if (id === 'companies') p.delete('type'); else p.set('type', id)
      // Reset pagination for the tab being opened
      if (id === 'companies') p.delete('page'); else p.delete('ppage')
      return p
    })
  }

  function goToCompanyPage(next: number) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      if (next <= 1) p.delete('page'); else p.set('page', String(next))
      return p
    })
  }

  function goToPersonPage(next: number) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      if (next <= 1) p.delete('ppage'); else p.set('ppage', String(next))
      return p
    })
  }

  const tabs: TabDef[] = [
    {
      id: 'companies',
      label: 'Virksomheder',
      count: loadingCompanies ? null : companyResult.totalCount,
    },
    {
      id: 'persons',
      label: 'Personer',
      count: loadingPersons ? null : personResult.totalCount,
    },
  ]

  const companyMaxPage = Math.max(1, Math.ceil(companyResult.totalCount / PAGE_SIZE))
  const personMaxPage  = Math.max(1, Math.ceil(personResult.totalCount  / PAGE_SIZE))

  return (
    <Container width="prose" className="py-10">
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-widest text-signal mb-2">
          Søgning
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl text-ink leading-tight">
          Find virksomheder og personer
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="mb-6">
        <label htmlFor="results-search" className="sr-only">
          CVR-nummer, virksomhedsnavn eller personnavn
        </label>
        <div className="flex border-2 border-ink rounded-sm overflow-hidden bg-paper focus-within:ring-2 focus-within:ring-signal">
          <input
            id="results-search"
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="CVR-nummer, virksomhedsnavn eller personnavn"
            className="flex-1 px-4 py-3 text-base bg-transparent focus:outline-none placeholder:text-ink-400"
          />
          <button
            type="submit"
            disabled={!draft.trim() || (loadingCompanies && loadingPersons)}
            className="px-5 sm:px-6 bg-ink text-paper hover:bg-ink-700 disabled:bg-ink-300 inline-flex items-center gap-2 transition-colors"
          >
            <Search className="h-5 w-5" aria-hidden />
            <span className="hidden sm:inline">Slå op</span>
          </button>
        </div>
      </form>

      {!query ? (
        <p className="text-sm text-ink-500 mt-4">
          Indtast et CVR-nummer (8 cifre) for direkte opslag, eller skriv et navn
          for at søge i virksomheder og personer.
        </p>
      ) : (
        <>
          <div className="mt-6 -mx-1 border-b border-rule">
            <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
              <Tabs
                tabs={tabs}
                active={activeTab}
                onChange={(id) => setTab(id as TabId)}
                ariaLabel="Søgeresultater"
                className="mt-0 flex-1 min-w-0 border-b-0"
              />
              <InactiveCompaniesToggle
                checked={includeInactive}
                onChange={setIncludeInactive}
              />
            </div>
          </div>

          <div className="mt-6">
            {activeTab === 'companies' && (
              <TabPanel
                loading={loadingCompanies}
                error={errorCompanies}
                empty={companyResult.companies.length === 0}
                emptyMessage={`Ingen virksomheder matchede "${query}".`}
                page={companyPage}
                maxPage={companyMaxPage}
                totalCount={companyResult.totalCount}
                pageSize={PAGE_SIZE}
                onPrev={() => goToCompanyPage(companyPage - 1)}
                onNext={() => goToCompanyPage(companyPage + 1)}
              >
                <ol className="list-none divide-y divide-rule border-t border-rule">
                  {companyResult.companies.map((c) => (
                    <CompanyRow key={c.cvrNummer} company={c} />
                  ))}
                </ol>
              </TabPanel>
            )}

            {activeTab === 'persons' && (
              <TabPanel
                loading={loadingPersons}
                error={errorPersons}
                empty={personResult.persons.length === 0}
                emptyMessage={`Ingen personer matchede "${query}".`}
                page={personPage}
                maxPage={personMaxPage}
                totalCount={personResult.totalCount}
                pageSize={PAGE_SIZE}
                onPrev={() => goToPersonPage(personPage - 1)}
                onNext={() => goToPersonPage(personPage + 1)}
              >
                <ol className="list-none divide-y divide-rule border-t border-rule">
                  {personResult.persons.map((p) => (
                    <PersonRow key={String(p.enhedsNummer)} person={p} />
                  ))}
                </ol>
              </TabPanel>
            )}
          </div>
        </>
      )}
    </Container>
  )
}

// ── Inactive companies toggle (search tab bar) ────────────────────────────

const InactiveCompaniesToggle: React.FC<{
  checked: boolean
  onChange: (next: boolean) => void
}> = ({ checked, onChange }) => {
  const id = React.useId()
  return (
    <div className="flex shrink-0 items-center gap-2.5 px-3 pb-3">
      <label htmlFor={id} className="text-sm text-ink-600 cursor-pointer select-none whitespace-nowrap">
        Inkluder inaktive
      </label>
      <Switch
        id={id}
        checked={checked}
        onChange={onChange}
        ariaLabel="Inkluder inaktive virksomheder"
      />
    </div>
  )
}

// ── Tab panel shell ───────────────────────────────────────────────────────

interface TabPanelProps {
  loading: boolean
  error: string | null
  empty: boolean
  emptyMessage: string
  page: number
  maxPage: number
  totalCount: number
  pageSize: number
  onPrev: () => void
  onNext: () => void
  children: React.ReactNode
}

const TabPanel: React.FC<TabPanelProps> = ({
  loading, error, empty, emptyMessage,
  page, maxPage, totalCount, pageSize,
  onPrev, onNext, children,
}) => {
  const rangeFrom = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const rangeTo   = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount)
  const hasPrev   = page > 1
  const hasNext   = totalCount > 0 && page < maxPage

  if (loading) {
    return (
      <p className="font-mono text-xs uppercase tracking-widest text-ink-400">
        Søger…
      </p>
    )
  }

  if (error) {
    return (
      <p className="text-sm text-signal-900 bg-signal-50 border border-signal-200 rounded-sm px-3 py-2">
        {error}
      </p>
    )
  }

  if (empty) {
    return <p className="text-sm text-ink-500">{emptyMessage}</p>
  }

  return (
    <>
      {children}
      {maxPage > 1 && (
        <nav
          className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-rule pt-4"
          aria-label="Sideinddeling"
        >
          <button
            type="button"
            onClick={onPrev}
            disabled={!hasPrev}
            className="inline-flex items-center gap-1.5 text-sm font-mono text-ink border border-rule rounded-sm px-3 py-2 hover:bg-ink-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            Forrige
          </button>
          <span className="text-sm text-ink-500 tabular-nums">
            Viser {rangeFrom}–{rangeTo} af {totalCount}
          </span>
          <button
            type="button"
            onClick={onNext}
            disabled={!hasNext}
            className="inline-flex items-center gap-1.5 text-sm font-mono text-ink border border-rule rounded-sm px-3 py-2 hover:bg-ink-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            Næste
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </nav>
      )}
    </>
  )
}

// ── Company row ───────────────────────────────────────────────────────────

const CompanyRow: React.FC<{ company: Company }> = ({ company }) => {
  const fullAddress = formatCompanyAddress(company)
  const country = detectCountry(company.adresse)
  const flagCode = companyCountryFlagCode(company)
  const start = formatDate(company.startdato)
  const showStatus = company.status && (isDissolvedStatus(company.status) || isDistressedStatus(company.status))
  const statusLabel = showStatus ? formatStatusLabel(company.status) : null
  const meta = [
    `CVR ${company.cvrNummer}`,
    company.virksomhedsform,
    start && `Stiftet ${start}`,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <li className="group">
      <Link
        to={`/company/${company.cvrNummer}`}
        className="block py-4 -mx-2 px-2 rounded-sm hover:bg-ink-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-ink-400 transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="font-serif text-lg text-ink group-hover:underline decoration-signal underline-offset-4 inline-flex items-baseline gap-1 flex-wrap">
              {company.navn || <em className="text-ink-400">Uden navn</em>}
              {flagCode && (
                <CountryFlagIcon code={flagCode} className="text-base" />
              )}
              {statusLabel && (
                <span
                  className={`inline-flex shrink-0 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide leading-tight rounded-sm border ${
                    isDissolvedStatus(company.status)
                      ? 'bg-signal-100 text-signal-900 border-signal-300'
                      : 'bg-paper-200 text-ink-700 border-rule'
                  }`}
                >
                  {statusLabel}
                </span>
              )}
              <WatchlistIndicator
                entityType="company"
                entityId={String(company.cvrNummer)}
              />
            </h3>
            <p className="mt-1 font-mono text-xs tabular-nums text-ink-500">{meta}</p>
            {company.adresse && (
              <p className="mt-1 text-sm text-ink-700">
                {stripTrailingCountryFromAddress(
                  formatAddressCountryLabel(fullAddress),
                  country.isForeign ? country.countryCode : undefined
                )}
              </p>
            )}
          </div>
          <ArrowRight
            aria-hidden
            className="h-4 w-4 mt-2 shrink-0 text-ink-300 group-hover:text-signal transition-colors"
          />
        </div>
      </Link>
    </li>
  )
}

// ── Person row ────────────────────────────────────────────────────────────

const PersonRow: React.FC<{ person: PersonSummary }> = ({ person }) => {
  const country = detectCountry(person.adresse)
  const flagCode = country.isForeign ? country.countryCode : null

  return (
    <li className="group">
      <Link
        to={`/person/${person.enhedsNummer}`}
        className="block py-4 -mx-2 px-2 rounded-sm hover:bg-ink-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-ink-400 transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <User
                aria-hidden
                className="h-3.5 w-3.5 shrink-0 text-ink-400 mt-0.5"
              />
              <h3 className="font-serif text-lg text-ink group-hover:underline decoration-signal underline-offset-4 inline-flex items-baseline gap-1">
                {person.navn || <em className="text-ink-400">Uden navn</em>}
                {flagCode && (
                  <CountryFlagIcon code={flagCode} className="text-base" />
                )}
                <WatchlistIndicator
                  entityType="person"
                  entityId={String(person.enhedsNummer)}
                />
              </h3>
            </div>
            <p className="mt-1 font-mono text-xs tabular-nums text-ink-500 pl-5">
              {person.isAddressConfidential
                ? 'Beskyttet adresse'
                : person.adresse
                ? stripTrailingCountryFromAddress(
                    formatAddressCountryLabel(person.adresse),
                    country.isForeign ? country.countryCode : undefined
                  )
                : null}
            </p>
          </div>
          <ArrowRight
            aria-hidden
            className="h-4 w-4 mt-2 shrink-0 text-ink-300 group-hover:text-signal transition-colors"
          />
        </div>
      </Link>
    </li>
  )
}

export default SearchPage
